import { useRef, useState } from "react";
import { Download, Trash2, Upload, X } from "lucide-react";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { Dialog } from "../../components/Dialog";
import { FormField } from "../../components/FormField";
import { Notice } from "../../components/Notice";
import { PageHeader } from "../../components/PageHeader";
import { RadioGroup } from "../../components/RadioGroup";
import { clearLocalData } from "../../lib/db";
import {
  buildExportEnvelope,
  importEnvelope,
  parseExportEnvelope,
  previewEnvelope,
  type ImportPreview,
} from "../../lib/exportImport";

type ImportMode = "merge" | "replace";

export function DataPage() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmExport, setConfirmExport] = useState(false);
  const [pendingImport, setPendingImport] = useState<{
    envelope: ReturnType<typeof parseExportEnvelope>;
    preview: ImportPreview;
  } | null>(null);
  const [importMode, setImportMode] = useState<ImportMode>("merge");
  const [resetArmed, setResetArmed] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  async function exportData() {
    const envelope = await buildExportEnvelope();
    const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `clearscope-export-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    setConfirmExport(false);
    setStatus("Export generated. Store it somewhere you control.");
  }

  async function stageImport(file: File) {
    setError(null);
    setStatus(null);
    try {
      const raw = await file.text();
      const envelope = parseExportEnvelope(raw);
      setPendingImport({ envelope, preview: previewEnvelope(envelope) });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Import failed.");
    }
  }

  async function commitImport() {
    if (!pendingImport) {
      return;
    }
    await importEnvelope(pendingImport.envelope, importMode);
    setStatus(`Import complete using ${importMode} mode.`);
    setPendingImport(null);
  }

  async function resetData() {
    if (deleteConfirm !== "DELETE") {
      setError("Type DELETE to confirm local data reset.");
      return;
    }
    await clearLocalData();
    setStatus("Local data cleared. Reload the app to reseed the demo board.");
    setDeleteConfirm("");
    setResetArmed(false);
  }

  return (
    <div className="space-y-7">
      <PageHeader eyebrow="Export" title="Export and import your data">
        Download a backup, move records to another browser, or clear this device when needed.
      </PageHeader>
      {status ? (
        <Notice title="Status" tone="success">
          {status}
        </Notice>
      ) : null}
      {error ? (
        <Notice title="Data operation blocked" tone="danger">
          {error}
        </Notice>
      ) : null}

      <div className="grid gap-5 xl:grid-cols-2">
        <Card accentEdge="left" className="grid gap-4" padding="lg" variant="surface">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-surface-raised text-accent-700">
              <Download aria-hidden="true" className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <div>
              <h2 className="card-title">Export your data</h2>
              <p className="mt-2 text-sm leading-6 text-text-muted">
                Exports include jobs, contractor profiles, saved jobs, and settings. The file may
                contain personal data.
              </p>
            </div>
          </div>
          {confirmExport ? (
            <Notice title="Export privacy reminder" tone="warning">
              This JSON file is readable by anyone who can access it. Store it in a protected
              location and do not share it casually.
            </Notice>
          ) : null}
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => (confirmExport ? void exportData() : setConfirmExport(true))}>
              {confirmExport ? "Confirm and download" : "Prepare JSON export"}
            </Button>
            {confirmExport ? (
              <Button
                aria-label="Cancel export"
                variant="secondary"
                onClick={() => setConfirmExport(false)}
              >
                <X aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                Cancel
              </Button>
            ) : null}
          </div>
        </Card>

        <Card accentEdge="left" className="grid gap-4" padding="lg" variant="surface">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-surface-raised text-accent-700">
              <Upload aria-hidden="true" className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <div>
              <h2 className="card-title">Import a job brief</h2>
              <p className="mt-2 text-sm leading-6 text-text-muted">
                Imports are checked before they are saved. Only import files from sources you trust.
              </p>
            </div>
          </div>
          <input
            ref={fileInputRef}
            aria-label="Select JSON import file"
            className="sr-only"
            id="import-file"
            type="file"
            accept="application/json,.json"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              if (file) {
                void stageImport(file);
              }
            }}
          />
          <button
            className="rounded-card border border-dashed border-border-strong bg-surface-raised p-6 text-center text-sm font-semibold text-text-muted transition hover:border-accent-300 hover:bg-surface"
            type="button"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const file = event.dataTransfer.files[0];
              if (file) {
                void stageImport(file);
              }
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            Select or drop a JSON export
          </button>
        </Card>
        <Card
          className="relative grid gap-4 overflow-hidden xl:col-span-2"
          padding="lg"
          tone="danger"
          variant="quiet"
        >
          <Notice title="Destructive local action" tone="danger">
            Reset clears ClearScope data from this browser profile only.
          </Notice>
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-danger-border bg-danger-bg text-danger">
              <Trash2 aria-hidden="true" className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <div>
              <h2 className="card-title">Reset this device</h2>
              <p className="mt-2 text-sm leading-6 text-text-muted">
                This clears ClearScope data from this browser profile. It does not affect any export
                file you already downloaded.
              </p>
            </div>
          </div>
          {resetArmed ? (
            <div className="grid gap-3 rounded-card border border-danger-border bg-surface-raised p-4 shadow-[var(--shadow-card)]">
              <FormField
                id="delete-confirm"
                label="Type DELETE to confirm"
                value={deleteConfirm}
                onChange={(event) => setDeleteConfirm(event.currentTarget.value)}
              />
              <div className="flex flex-wrap gap-3">
                <Button variant="danger" onClick={() => void resetData()}>
                  Clear local data
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setResetArmed(false);
                    setDeleteConfirm("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Button variant="danger" onClick={() => setResetArmed(true)}>
                Begin reset
              </Button>
            </div>
          )}
        </Card>
      </div>
      <Dialog
        actions={
          <>
            <Button onClick={() => void commitImport()}>Commit import</Button>
            <Button variant="secondary" onClick={() => setPendingImport(null)}>
              Cancel
            </Button>
          </>
        }
        description="Review the validated export before writing it into this browser profile."
        onOpenChange={(open) => {
          if (!open) {
            setPendingImport(null);
          }
        }}
        open={Boolean(pendingImport)}
        title="Import preview"
      >
        {pendingImport ? (
          <div className="grid gap-4 text-sm leading-6 text-text-muted">
            <div className="card-inset grid gap-2 p-4">
              <p>Schema version: {pendingImport.preview.schemaVersion}</p>
              <p>Exported at: {new Date(pendingImport.preview.exportedAt).toLocaleString()}</p>
              <p>
                {pendingImport.preview.jobs} jobs, {pendingImport.preview.contractors} contractors,{" "}
                {pendingImport.preview.savedJobs} saved jobs
              </p>
            </div>
            <RadioGroup<ImportMode>
              legend="Mode"
              name="import-mode"
              onChange={setImportMode}
              options={[
                { value: "merge", label: "Merge into current data" },
                { value: "replace", label: "Replace jobs, contractors, and saved jobs" },
              ]}
              value={importMode}
            />
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
